import React, { useEffect, useState, useCallback } from "react";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Plus,
  Target,
  Trophy,
  MessagesSquare,
  Trash2,
  CheckCircle2,
  Circle,
  PauseCircle,
  Sparkles,
} from "lucide-react";

const areaOptions = [
  "Daily Living",
  "Social",
  "Community",
  "Health",
  "Work",
  "Learning",
];

const moodOptions = [
  { value: "proud", label: "Proud" },
  { value: "happy", label: "Happy" },
  { value: "steady", label: "Steady" },
  { value: "tough", label: "Tough day" },
];

const audienceOptions = [
  { value: "team", label: "Whole team" },
  { value: "family", label: "Family" },
  { value: "provider", label: "Providers" },
];

const statusStyles = {
  active: "bg-brand-teal/10 text-brand-tealDark border-brand-teal/30",
  achieved: "bg-brand-magenta/10 text-brand-magentaDark border-brand-magenta/30",
  paused: "bg-brand-navy/5 text-brand-mute border-brand-navy/15",
};

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [goals, setGoals] = useState([]);
  const [wins, setWins] = useState([]);
  const [notes, setNotes] = useState([]);
  const [tab, setTab] = useState("goals");

  const refresh = useCallback(async () => {
    try {
      const [s, g, w, n] = await Promise.all([
        api.get("/stats"),
        api.get("/goals"),
        api.get("/wins"),
        api.get("/notes"),
      ]);
      setStats(s.data);
      setGoals(g.data);
      setWins(w.data);
      setNotes(n.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not load your dashboard.");
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="min-h-screen bg-brand-sand text-brand-navy">
      <Navbar variant="dashboard" />

      <main className="mx-auto max-w-7xl px-5 lg:px-10 py-10">
        {/* Greeting */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-brand-magenta">Your space</p>
            <h1 className="mt-2 font-display font-bold text-3xl sm:text-4xl tracking-tight" data-testid="dashboard-greeting">
              Hi {user?.name?.split(" ")[0] || "there"} — capacity is built every day.
            </h1>
            <p className="mt-2 text-brand-mute max-w-2xl">
              Track the goals that matter, celebrate the wins, keep your whole team pointed the same way.
            </p>
          </div>
          <QuickWinButton onCreated={refresh} goals={goals} />
        </div>

        {/* Stats */}
        <div className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Active goals" value={stats?.goals_active ?? "—"} icon={Target} tint="teal" data-testid="stat-active-goals" />
          <StatCard label="Wins this week" value={stats?.wins_this_week ?? "—"} icon={Trophy} tint="magenta" data-testid="stat-wins-week" />
          <StatCard label="Goals achieved" value={stats?.goals_achieved ?? "—"} icon={CheckCircle2} tint="teal" data-testid="stat-goals-achieved" />
          <StatCard label="Shared notes" value={stats?.notes_total ?? "—"} icon={MessagesSquare} tint="magenta" data-testid="stat-notes-total" />
        </div>

        {/* Tabs */}
        <div className="mt-10">
          <Tabs value={tab} onValueChange={setTab} className="w-full">
            <TabsList className="bg-white border border-brand-navy/10 rounded-full p-1 h-11">
              <TabsTrigger value="goals" className="rounded-full px-5 data-[state=active]:bg-brand-navy data-[state=active]:text-white" data-testid="tab-goals">Goals</TabsTrigger>
              <TabsTrigger value="wins" className="rounded-full px-5 data-[state=active]:bg-brand-navy data-[state=active]:text-white" data-testid="tab-wins">Daily wins</TabsTrigger>
              <TabsTrigger value="notes" className="rounded-full px-5 data-[state=active]:bg-brand-navy data-[state=active]:text-white" data-testid="tab-notes">Shared notes</TabsTrigger>
            </TabsList>

            <TabsContent value="goals" className="mt-6">
              <GoalsPanel goals={goals} onChanged={refresh} />
            </TabsContent>
            <TabsContent value="wins" className="mt-6">
              <WinsPanel wins={wins} goals={goals} onChanged={refresh} />
            </TabsContent>
            <TabsContent value="notes" className="mt-6">
              <NotesPanel notes={notes} onChanged={refresh} />
            </TabsContent>
          </Tabs>
        </div>
      </main>
    </div>
  );
}

function StatCard({ label, value, icon: Icon, tint, ...rest }) {
  const color = tint === "teal" ? "text-brand-teal bg-brand-teal/10" : "text-brand-magenta bg-brand-magenta/10";
  return (
    <div className="rounded-2xl bg-white border border-brand-navy/10 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" {...rest}>
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ${color}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-4 font-display font-bold text-3xl tracking-tight">{value}</div>
      <div className="mt-1 text-sm text-brand-mute">{label}</div>
    </div>
  );
}

// ---------- Goals ----------
function GoalsPanel({ goals, onChanged }) {
  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-2xl tracking-tight">Goals</h2>
        <NewGoalDialog onCreated={onChanged} />
      </div>
      {goals.length === 0 ? (
        <EmptyState
          icon={Target}
          title="No goals yet"
          body="Add the first goal — something practical you'd like to build toward this month."
        />
      ) : (
        <div className="grid md:grid-cols-2 gap-4">
          {goals.map((g) => (
            <GoalCard key={g.id} goal={g} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

function GoalCard({ goal, onChanged }) {
  const cycle = async (status) => {
    try {
      await api.patch(`/goals/${goal.id}`, { status });
      toast.success(status === "achieved" ? "Marked as achieved 🎉" : "Updated");
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const remove = async () => {
    if (!window.confirm("Delete this goal?")) return;
    try {
      await api.delete(`/goals/${goal.id}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div className="rounded-2xl bg-white border border-brand-navy/10 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`goal-card-${goal.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <Badge variant="outline" className={`text-[10px] uppercase tracking-widest ${statusStyles[goal.status]}`}>
            {goal.status}
          </Badge>
          <h3 className="mt-3 font-display font-bold text-lg tracking-tight">{goal.title}</h3>
          <div className="mt-1 text-xs text-brand-mute">{goal.area}</div>
        </div>
        <button
          onClick={remove}
          className="text-brand-mute hover:text-red-500 transition-colors p-1"
          aria-label="Delete goal"
          data-testid={`goal-delete-${goal.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      {goal.description && <p className="mt-3 text-sm text-brand-mute leading-relaxed">{goal.description}</p>}
      <div className="mt-5 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant={goal.status === "active" ? "default" : "outline"}
          className={`rounded-full ${goal.status === "active" ? "bg-brand-teal hover:bg-brand-tealDark text-white" : ""}`}
          onClick={() => cycle("active")}
          data-testid={`goal-status-active-${goal.id}`}
        >
          <Circle className="h-3.5 w-3.5 mr-1.5" /> Active
        </Button>
        <Button
          size="sm"
          variant={goal.status === "achieved" ? "default" : "outline"}
          className={`rounded-full ${goal.status === "achieved" ? "bg-brand-magenta hover:bg-brand-magentaDark text-white" : ""}`}
          onClick={() => cycle("achieved")}
          data-testid={`goal-status-achieved-${goal.id}`}
        >
          <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Achieved
        </Button>
        <Button
          size="sm"
          variant={goal.status === "paused" ? "default" : "outline"}
          className="rounded-full"
          onClick={() => cycle("paused")}
          data-testid={`goal-status-paused-${goal.id}`}
        >
          <PauseCircle className="h-3.5 w-3.5 mr-1.5" /> Paused
        </Button>
      </div>
    </div>
  );
}

function NewGoalDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [area, setArea] = useState("Daily Living");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/goals", { title, description, area });
      toast.success("Goal added");
      setTitle("");
      setDescription("");
      setArea("Daily Living");
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white btn-lift" data-testid="new-goal-open">
          <Plus className="h-4 w-4 mr-1.5" /> New goal
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="new-goal-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Add a goal</DialogTitle>
          <DialogDescription>Break a big NDIS goal into something practical to work on this month.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="g-title">Goal</Label>
            <Input id="g-title" value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="new-goal-title" placeholder="e.g. Catch the bus independently to the library" className="mt-2 h-11" />
          </div>
          <div>
            <Label>Life area</Label>
            <Select value={area} onValueChange={setArea}>
              <SelectTrigger className="mt-2 h-11" data-testid="new-goal-area">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {areaOptions.map((a) => (
                  <SelectItem key={a} value={a}>{a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="g-desc">Why this matters</Label>
            <Textarea id="g-desc" value={description} onChange={(e) => setDescription(e.target.value)} rows={3} data-testid="new-goal-desc" placeholder="Why this goal is important — in plain words." className="mt-2" />
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full h-11 bg-brand-teal hover:bg-brand-tealDark text-white btn-lift" data-testid="new-goal-submit">
            {busy ? "Saving…" : "Add goal"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Wins ----------
function WinsPanel({ wins, goals, onChanged }) {
  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-2xl tracking-tight">Daily wins</h2>
        <NewWinDialog goals={goals} onCreated={onChanged} />
      </div>
      {wins.length === 0 ? (
        <EmptyState
          icon={Trophy}
          title="No wins logged yet"
          body="Wins are the small moments that prove capacity is being built. Log the first one."
        />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 stagger">
          {wins.map((w) => (
            <WinCard key={w.id} win={w} goals={goals} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

function WinCard({ win, goals, onChanged }) {
  const linkedGoal = goals.find((g) => g.id === win.goal_id);
  const remove = async () => {
    try {
      await api.delete(`/wins/${win.id}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  return (
    <div className="rounded-2xl bg-white border border-brand-navy/10 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`win-card-${win.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-brand-magenta/10 text-brand-magenta">
          <Trophy className="h-4 w-4" />
        </div>
        <button onClick={remove} className="text-brand-mute hover:text-red-500 transition-colors p-1" aria-label="Delete win" data-testid={`win-delete-${win.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <h3 className="mt-4 font-display font-bold text-lg tracking-tight">{win.title}</h3>
      {win.detail && <p className="mt-2 text-sm text-brand-mute leading-relaxed">{win.detail}</p>}
      <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-brand-mute">
        <Badge variant="outline" className="rounded-full capitalize">{win.mood}</Badge>
        {linkedGoal && (
          <Badge variant="outline" className="rounded-full bg-brand-teal/10 text-brand-tealDark border-brand-teal/30">
            <Target className="h-3 w-3 mr-1" /> {linkedGoal.title}
          </Badge>
        )}
        <span>{new Date(win.created_at).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

function NewWinDialog({ goals, onCreated, triggerTestId = "new-win-open" }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [detail, setDetail] = useState("");
  const [mood, setMood] = useState("proud");
  const [goalId, setGoalId] = useState("none");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/wins", {
        title,
        detail,
        mood,
        goal_id: goalId === "none" ? null : goalId,
      });
      toast.success("Win logged. Keep going.");
      setTitle("");
      setDetail("");
      setMood("proud");
      setGoalId("none");
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-brand-magenta hover:bg-brand-magentaDark text-white btn-lift" data-testid={triggerTestId}>
          <Sparkles className="h-4 w-4 mr-1.5" /> Log a win
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="new-win-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Log a win</DialogTitle>
          <DialogDescription>Small moments count. Capture what happened today and how it felt.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="w-title">What happened?</Label>
            <Input id="w-title" value={title} onChange={(e) => setTitle(e.target.value)} required data-testid="new-win-title" placeholder="e.g. Made breakfast on their own" className="mt-2 h-11" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Mood</Label>
              <Select value={mood} onValueChange={setMood}>
                <SelectTrigger className="mt-2 h-11" data-testid="new-win-mood">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {moodOptions.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Link to goal</Label>
              <Select value={goalId} onValueChange={setGoalId}>
                <SelectTrigger className="mt-2 h-11" data-testid="new-win-goal">
                  <SelectValue placeholder="Optional" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No goal</SelectItem>
                  {goals.map((g) => (
                    <SelectItem key={g.id} value={g.id}>{g.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label htmlFor="w-detail">A little more (optional)</Label>
            <Textarea id="w-detail" value={detail} onChange={(e) => setDetail(e.target.value)} rows={3} data-testid="new-win-detail" placeholder="What made this feel like a win?" className="mt-2" />
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full h-11 bg-brand-magenta hover:bg-brand-magentaDark text-white btn-lift" data-testid="new-win-submit">
            {busy ? "Saving…" : "Log win"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function QuickWinButton({ onCreated, goals }) {
  return <NewWinDialog onCreated={onCreated} goals={goals} triggerTestId="quick-win-open" />;
}

// ---------- Notes ----------
function NotesPanel({ notes, onChanged }) {
  return (
    <div className="grid gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-2xl tracking-tight">Shared notes</h2>
        <NewNoteDialog onCreated={onChanged} />
      </div>
      {notes.length === 0 ? (
        <EmptyState
          icon={MessagesSquare}
          title="No notes yet"
          body="Notes keep everyone — family, providers, support workers — pointed at the same outcome."
        />
      ) : (
        <div className="grid gap-3">
          {notes.map((n) => (
            <NoteCard key={n.id} note={n} onChanged={onChanged} />
          ))}
        </div>
      )}
    </div>
  );
}

function NoteCard({ note, onChanged }) {
  const remove = async () => {
    try {
      await api.delete(`/notes/${note.id}`);
      onChanged();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const audienceColor = {
    family: "bg-brand-magenta/10 text-brand-magentaDark border-brand-magenta/30",
    provider: "bg-brand-teal/10 text-brand-tealDark border-brand-teal/30",
    team: "bg-brand-navy/5 text-brand-navy border-brand-navy/15",
  }[note.audience];
  return (
    <div className="rounded-2xl bg-white border border-brand-navy/10 p-5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]" data-testid={`note-card-${note.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className={`rounded-full capitalize ${audienceColor}`}>{note.audience}</Badge>
          <span className="text-xs text-brand-mute">{note.author_name} · {note.author_role}</span>
        </div>
        <button onClick={remove} className="text-brand-mute hover:text-red-500 transition-colors p-1" aria-label="Delete note" data-testid={`note-delete-${note.id}`}>
          <Trash2 className="h-4 w-4" />
        </button>
      </div>
      <p className="mt-3 text-brand-navy/90 leading-relaxed whitespace-pre-wrap">{note.body}</p>
      <div className="mt-3 text-xs text-brand-mute">{new Date(note.created_at).toLocaleString()}</div>
    </div>
  );
}

function NewNoteDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState("");
  const [audience, setAudience] = useState("team");
  const [role, setRole] = useState("family");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/notes", { body, audience, author_role: role });
      toast.success("Note shared with the team");
      setBody("");
      setAudience("team");
      setRole("family");
      setOpen(false);
      onCreated();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="rounded-full bg-brand-navy hover:bg-brand-navy/90 text-white btn-lift" data-testid="new-note-open">
          <Plus className="h-4 w-4 mr-1.5" /> New note
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg" data-testid="new-note-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl tracking-tight">Share a note</DialogTitle>
          <DialogDescription>Keep family, providers and the whole team pointed at the same outcome.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="n-body">Note</Label>
            <Textarea id="n-body" value={body} onChange={(e) => setBody(e.target.value)} rows={5} required data-testid="new-note-body" placeholder="Strategy, context, an update from the week…" className="mt-2" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label>Audience</Label>
              <Select value={audience} onValueChange={setAudience}>
                <SelectTrigger className="mt-2 h-11" data-testid="new-note-audience">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {audienceOptions.map((a) => (
                    <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Your role</Label>
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger className="mt-2 h-11" data-testid="new-note-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="family">Family</SelectItem>
                  <SelectItem value="participant">Participant</SelectItem>
                  <SelectItem value="support-worker">Support Worker</SelectItem>
                  <SelectItem value="therapist">Therapist</SelectItem>
                  <SelectItem value="coordinator">Coordinator</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <Button type="submit" disabled={busy} className="w-full rounded-full h-11 bg-brand-teal hover:bg-brand-tealDark text-white btn-lift" data-testid="new-note-submit">
            {busy ? "Sharing…" : "Share note"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Empty state ----------
function EmptyState({ icon: Icon, title, body }) {
  return (
    <div className="rounded-2xl border border-dashed border-brand-navy/15 bg-white/60 p-12 text-center" data-testid="empty-state">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand-teal/10 text-brand-teal">
        <Icon className="h-6 w-6" />
      </div>
      <h3 className="mt-4 font-display font-bold text-xl tracking-tight">{title}</h3>
      <p className="mt-2 text-brand-mute max-w-md mx-auto">{body}</p>
    </div>
  );
}
